import React from "react";
import { useSelector } from "react-redux";

import {
	Avatar,
	type ICellRendererParams,
	Link,
	type PYColumnDef,
	type SortChangedEvent,
	Table,
	Text,
} from "@performyard/py-components";
import type { EmployeeMeetingStatsRowJson } from "@performyard/service-contracts";

import { getUserID } from "../../../selectors/app";

import { employeeDetailsLinkWithFrom } from "../../../utils/employeeDetailsBreadcrumbs";

interface CompanyMeetingsCoverageTableProps {
	rows: EmployeeMeetingStatsRowJson[];
	isFetching: boolean;
	showTeamMeetings: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
}

type NameCellRendererParams = ICellRendererParams<EmployeeMeetingStatsRowJson> & { currentUserId?: string };

const EmployeeCellRenderer = (params: NameCellRendererParams) => {
	const row = params.data;
	if (!row) return null;
	return (
		<div className="h-full w-full min-w-0">
			<Link
				href={employeeDetailsLinkWithFrom(row.employeeId, params.currentUserId)}
				forceHardReload
				underline="hover"
			>
				<div className="flex h-full w-full min-w-0 items-center gap-2">
					<Avatar id={row.employeeId} name={row.employeeName} size={24} />
					<Text
						size="paragraph-3"
						color="primary-5"
						weight="regular"
						dangerouslySetClassName="py-line-clamp-1 min-w-0"
					>
						{row.employeeName}
					</Text>
				</div>
			</Link>
		</div>
	);
};

const ManagerCellRenderer = (params: NameCellRendererParams) => {
	const row = params.data;
	if (!row) return null;
	if (!row.managerId || !row.managerName) {
		return (
			<div className="flex h-full w-full items-center">
				<Text size="paragraph-3" weight="regular">
					—
				</Text>
			</div>
		);
	}
	return (
		<div className="h-full w-full min-w-0">
			<Link
				href={employeeDetailsLinkWithFrom(row.managerId, params.currentUserId)}
				forceHardReload
				underline="hover"
			>
				<div className="flex h-full w-full min-w-0 items-center gap-2">
					<Avatar id={row.managerId} name={row.managerName} size={24} />
					<Text
						size="paragraph-3"
						color="primary-5"
						weight="regular"
						dangerouslySetClassName="py-line-clamp-1 min-w-0"
					>
						{row.managerName}
					</Text>
				</div>
			</Link>
		</div>
	);
};

const GroupCellRenderer = (params: ICellRendererParams<EmployeeMeetingStatsRowJson>) => {
	const row = params.data;
	if (!row) return null;
	return (
		<div className="flex h-full w-full min-w-0 items-center">
			<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1 min-w-0">
				{row.group || "—"}
			</Text>
		</div>
	);
};

const NumberCellRenderer = (params: ICellRendererParams<EmployeeMeetingStatsRowJson>) => {
	const row = params.data;
	const field = params.colDef?.field as keyof EmployeeMeetingStatsRowJson | undefined;
	if (!row || !field) return null;
	const value = row[field];
	return (
		<div className="flex h-full w-full items-center justify-center">
			<Text size="paragraph-3" weight="regular">
				{typeof value === "number" ? value : "—"}
			</Text>
		</div>
	);
};

const NUMBER_HEADER_CLASS = "[&_.ag-header-cell-label]:justify-center";
const NUMBER_CELL_CLASS = "flex items-center justify-center";

const getColumnDefs = (
	showTeamMeetings: boolean,
	currentUserId?: string
): PYColumnDef<EmployeeMeetingStatsRowJson>[] => {
	const columns: PYColumnDef<EmployeeMeetingStatsRowJson>[] = [
		{
			field: "employeeName",
			headerName: "Employee Name",
			sortable: true,
			resizable: false,
			minWidth: 200,
			cellClass: "flex items-center",
			cellRenderer: EmployeeCellRenderer,
			cellRendererParams: { currentUserId },
		},
		{
			field: "managerName",
			headerName: "Manager Name",
			sortable: true,
			resizable: false,
			minWidth: 200,
			cellClass: "flex items-center",
			cellRenderer: ManagerCellRenderer,
			cellRendererParams: { currentUserId },
		},
		{
			field: "group",
			headerName: "Group",
			sortable: true,
			resizable: false,
			minWidth: 160,
			cellClass: "flex items-center",
			cellRenderer: GroupCellRenderer,
		},
		{
			field: "workspaceCount",
			headerName: "Meeting Workspaces",
			sortable: true,
			resizable: false,
			minWidth: 160,
			headerClass: NUMBER_HEADER_CLASS,
			cellClass: NUMBER_CELL_CLASS,
			cellRenderer: NumberCellRenderer,
		},
		{
			field: "allMeetingsCount",
			headerName: "All Meetings",
			sortable: true,
			resizable: false,
			minWidth: 140,
			headerClass: NUMBER_HEADER_CLASS,
			cellClass: NUMBER_CELL_CLASS,
			cellRenderer: NumberCellRenderer,
		},
		{
			field: "oneOnOneWithManagerCount",
			headerName: "1:1s with Manager",
			sortable: true,
			resizable: false,
			minWidth: 160,
			headerClass: NUMBER_HEADER_CLASS,
			cellClass: NUMBER_CELL_CLASS,
			cellRenderer: NumberCellRenderer,
		},
	];

	if (showTeamMeetings) {
		columns.push({
			field: "teamMeetingsCount",
			headerName: "Team Meetings",
			sortable: true,
			resizable: false,
			minWidth: 140,
			headerClass: NUMBER_HEADER_CLASS,
			cellClass: NUMBER_CELL_CLASS,
			cellRenderer: NumberCellRenderer,
		});
	}

	return columns;
};

const CompanyMeetingsCoverageTable = (props: CompanyMeetingsCoverageTableProps) => {
	const { rows, isFetching, showTeamMeetings, activeSort, activeSortDirection, handleColumnHeaderClick } = props;
	const currentUserId = useSelector(getUserID);

	const onSortChanged = (event: SortChangedEvent<EmployeeMeetingStatsRowJson>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="companyMeetingsCoverageContainer">
			<Table<EmployeeMeetingStatsRowJson>
				autoHeight
				columnSizing="fit-grid"
				rowData={rows}
				columnDefs={getColumnDefs(showTeamMeetings, currentUserId)}
				loading={isFetching}
				getRowId={(params) => params.data.employeeId}
				onSortChanged={onSortChanged}
				emptyStateText="No Employees Found"
				initialState={{
					sort: {
						sortModel: activeSort
							? [{ colId: activeSort, sort: activeSortDirection as "asc" | "desc" }]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default CompanyMeetingsCoverageTable;
